import React, { useState, useEffect, useRef } from 'react';
import { 
  Image, Type, Plus, Edit2, Trash2, Eye, EyeOff, 
  ExternalLink, BarChart3, MousePointer, RefreshCw,
  Home, LayoutList, Calendar, Upload, X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { formatDistanceToNow } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BannerManagement = () => {
  const { token } = useAuth();
  const [banners, setBanners] = useState([]);
  const [stats, setStats] = useState(null);
  const [partnerships, setPartnerships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchBanners();
    fetchStats();
    fetchPartnerships();
  }, []);

  const fetchBanners = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/banners/admin/all`, { headers });
      setBanners(response.data);
    } catch (error) {
      console.error('Error fetching banners:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/banners/admin/stats`, { headers });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchPartnerships = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/partnerships/`);
      setPartnerships(response.data);
    } catch (error) {
      console.error('Error fetching partnerships:', error);
    }
  };

  const handleSeedBanners = async () => {
    try {
      await axios.post(`${API_URL}/api/banners/seed`, {}, { headers });
      toast.success('Sample banners created');
      fetchBanners();
      fetchStats();
    } catch (error) {
      toast.error('Failed to seed banners');
    }
  };

  const handleDelete = async (bannerId) => {
    try {
      await axios.delete(`${API_URL}/api/banners/${bannerId}`, { headers });
      toast.success('Banner deleted');
      fetchBanners();
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete banner');
    }
  };

  const handleToggleActive = async (banner) => {
    try {
      await axios.put(
        `${API_URL}/api/banners/${banner.id}`,
        { is_active: !banner.is_active },
        { headers }
      );
      toast.success(banner.is_active ? 'Banner deactivated' : 'Banner activated');
      fetchBanners();
      fetchStats();
    } catch (error) {
      toast.error('Failed to update banner');
    }
  };

  const openEditDialog = (banner = null) => {
    setEditingBanner(banner);
    setDialogOpen(true);
  };

  const getPlacementBadge = (placement) => {
    const variants = {
      homepage: 'bg-blue-100 text-blue-800',
      sidebar: 'bg-purple-100 text-purple-800',
      both: 'bg-green-100 text-green-800',
    };
    return <Badge className={variants[placement] || variants.both}>{placement}</Badge>;
  };

  const getTypeBadge = (type) => {
    return type === 'image' ? (
      <Badge variant="outline"><Image className="w-3 h-3 mr-1" /> Image</Badge>
    ) : (
      <Badge variant="outline"><Type className="w-3 h-3 mr-1" /> Text</Badge>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Image className="w-8 h-8 text-primary" />
              Partner Banners
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage promotional banners for partners and sponsors
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { fetchBanners(); fetchStats(); }}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            {banners.length === 0 && (
              <Button variant="outline" onClick={handleSeedBanners}>
                Seed Sample Banners
              </Button>
            )}
            <Button onClick={() => openEditDialog()}>
              <Plus className="w-4 h-4 mr-2" />
              New Banner
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold">{stats.total_banners}</p>
                <p className="text-sm text-muted-foreground">Total Banners</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-green-600">{stats.active_banners}</p>
                <p className="text-sm text-muted-foreground">Active</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-blue-600">{stats.homepage_banners}</p>
                <p className="text-sm text-muted-foreground">Homepage</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-purple-600">{stats.sidebar_banners}</p>
                <p className="text-sm text-muted-foreground">Sidebar</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold">{stats.total_impressions.toLocaleString()}</p>
                <p className="text-sm text-muted-foreground">Impressions</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold">{stats.total_clicks.toLocaleString()}</p>
                <p className="text-sm text-muted-foreground">Clicks</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Banners List */}
        {loading ? (
          <div className="text-center py-8">Loading...</div>
        ) : banners.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Image className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
              <h3 className="text-lg font-medium mb-2">No banners yet</h3>
              <p className="text-muted-foreground mb-4">
                Create your first partner banner or seed sample data
              </p>
              <div className="flex justify-center gap-2">
                <Button variant="outline" onClick={handleSeedBanners}>
                  Seed Sample Banners
                </Button>
                <Button onClick={() => openEditDialog()}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Banner
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {banners.map((banner) => (
              <Card key={banner.id} className={!banner.is_active ? 'opacity-60' : ''}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    {/* Preview */}
                    <div 
                      className="w-48 h-24 rounded-lg flex-shrink-0 flex items-center justify-center p-3"
                      style={{ 
                        backgroundColor: banner.background_color || '#1e40af',
                        color: banner.text_color || '#ffffff'
                      }}
                    >
                      <div className="text-center">
                        <p className="font-semibold text-xs line-clamp-2">{banner.title}</p>
                        <p className="text-[10px] opacity-75 mt-1">{banner.cta_text}</p>
                      </div>
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold truncate">{banner.title}</h3>
                        {!banner.is_active && <Badge variant="secondary">Inactive</Badge>}
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-1 mb-2">
                        {banner.description || 'No description'}
                      </p>
                      <div className="flex items-center gap-2 flex-wrap">
                        {getPlacementBadge(banner.placement)}
                        {getTypeBadge(banner.banner_type)}
                        <Badge variant="outline">Priority: {banner.priority}</Badge>
                        {banner.partnership_name && (
                          <Badge variant="outline">Partner: {banner.partnership_name}</Badge>
                        )}
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="text-right text-sm space-y-1">
                      <div className="flex items-center gap-1 justify-end text-muted-foreground">
                        <Eye className="w-4 h-4" />
                        {banner.impression_count.toLocaleString()}
                      </div>
                      <div className="flex items-center gap-1 justify-end text-muted-foreground">
                        <MousePointer className="w-4 h-4" />
                        {banner.click_count.toLocaleString()}
                      </div>
                      {banner.impression_count > 0 && (
                        <div className="text-xs text-muted-foreground">
                          CTR: {((banner.click_count / banner.impression_count) * 100).toFixed(1)}%
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleToggleActive(banner)}
                      >
                        {banner.is_active ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openEditDialog(banner)}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="ghost" className="text-destructive">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Banner?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will permanently delete "{banner.title}". This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDelete(banner.id)}>
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <BannerDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          banner={editingBanner}
          partnerships={partnerships}
          token={token}
          onSuccess={() => {
            fetchBanners();
            fetchStats();
            setDialogOpen(false);
            setEditingBanner(null);
          }}
        />
      </div>
    </div>
  );
};

// Banner Create/Edit Dialog
const BannerDialog = ({ open, onOpenChange, banner, partnerships, token, onSuccess }) => {
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    link_url: '',
    cta_text: 'Learn More',
    banner_type: 'text',
    placement: 'both',
    priority: 50,
    partnership_id: '',
    is_active: true,
    background_color: '#1e40af',
    text_color: '#ffffff',
    image_url: '',
  });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPG, PNG, GIF, or WebP.');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB.');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API_URL}/api/uploads/banner-image`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setFormData(prev => ({
        ...prev,
        image_url: `${API_URL}${response.data.url}`
      }));
      toast.success('Image uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = () => {
    setFormData(prev => ({ ...prev, image_url: '' }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  useEffect(() => {
    if (banner) {
      setFormData({
        title: banner.title || '',
        description: banner.description || '',
        link_url: banner.link_url || '',
        cta_text: banner.cta_text || 'Learn More',
        banner_type: banner.banner_type || 'text',
        placement: banner.placement || 'both',
        priority: banner.priority || 50,
        partnership_id: banner.partnership_id || '',
        is_active: banner.is_active ?? true,
        background_color: banner.background_color || '#1e40af',
        text_color: banner.text_color || '#ffffff',
        image_url: banner.image_url || '',
      });
    } else {
      setFormData({
        title: '',
        description: '',
        link_url: '',
        cta_text: 'Learn More',
        banner_type: 'text',
        placement: 'both',
        priority: 50,
        partnership_id: '',
        is_active: true,
        background_color: '#1e40af',
        text_color: '#ffffff',
        image_url: '',
      });
    }
  }, [banner, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.link_url.trim()) {
      toast.error('Title and Link URL are required');
      return;
    }

    setLoading(true);
    try {
      const data = {
        ...formData,
        partnership_id: formData.partnership_id || null,
        image_url: formData.image_url || null,
      };

      if (banner) {
        await axios.put(
          `${API_URL}/api/banners/${banner.id}`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Banner updated');
      } else {
        await axios.post(
          `${API_URL}/api/banners/`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Banner created');
      }
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save banner');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{banner ? 'Edit Banner' : 'Create New Banner'}</DialogTitle>
          <DialogDescription>
            Configure the partner banner settings and appearance
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Preview */}
          <div 
            className="rounded-lg p-4"
            style={{ 
              backgroundColor: formData.background_color,
              color: formData.text_color
            }}
          >
            <p className="font-semibold">{formData.title || 'Banner Title'}</p>
            <p className="text-sm opacity-90">{formData.description || 'Banner description'}</p>
            <p className="text-xs mt-2 opacity-75">{formData.cta_text} →</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Pet Insurance - 20% Off"
                maxLength={100}
              />
            </div>
            <div className="space-y-2">
              <Label>CTA Text</Label>
              <Input
                value={formData.cta_text}
                onChange={(e) => setFormData({ ...formData, cta_text: e.target.value })}
                placeholder="Learn More"
                maxLength={50}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Short promotional text..."
              rows={2}
              maxLength={500}
            />
          </div>

          <div className="space-y-2">
            <Label>Link URL *</Label>
            <Input
              value={formData.link_url}
              onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
              placeholder="https://partner.com/offer or /services"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={formData.banner_type} onValueChange={(v) => setFormData({ ...formData, banner_type: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="text">Text Banner</SelectItem>
                  <SelectItem value="image">Image Banner</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Placement</Label>
              <Select value={formData.placement} onValueChange={(v) => setFormData({ ...formData, placement: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="homepage">Homepage Only</SelectItem>
                  <SelectItem value="sidebar">Sidebar Only</SelectItem>
                  <SelectItem value="both">Both</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Priority (0-100)</Label>
              <Input
                type="number"
                min={0}
                max={100}
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Background Color</Label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={formData.background_color}
                  onChange={(e) => setFormData({ ...formData, background_color: e.target.value })}
                  className="w-10 h-10 rounded cursor-pointer"
                />
                <Input
                  value={formData.background_color}
                  onChange={(e) => setFormData({ ...formData, background_color: e.target.value })}
                  placeholder="#1e40af"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Text Color</Label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={formData.text_color}
                  onChange={(e) => setFormData({ ...formData, text_color: e.target.value })}
                  className="w-10 h-10 rounded cursor-pointer"
                />
                <Input
                  value={formData.text_color}
                  onChange={(e) => setFormData({ ...formData, text_color: e.target.value })}
                  placeholder="#ffffff"
                />
              </div>
            </div>
          </div>

          {formData.banner_type === 'image' && (
            <div className="space-y-2">
              <Label>Banner Image</Label>
              
              {/* Image Preview */}
              {formData.image_url && (
                <div className="relative inline-block">
                  <img 
                    src={formData.image_url} 
                    alt="Banner preview" 
                    className="max-h-32 rounded-lg border"
                  />
                  <Button
                    type="button"
                    size="icon"
                    variant="destructive"
                    className="absolute -top-2 -right-2 h-6 w-6"
                    onClick={removeImage}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              )}
              
              {/* Upload Button */}
              <div className="flex gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handleImageUpload}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {uploading ? 'Uploading...' : 'Upload Image'}
                </Button>
                <span className="text-xs text-muted-foreground self-center">
                  or enter URL below
                </span>
              </div>
              
              {/* URL Input */}
              <Input
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                placeholder="https://example.com/banner-image.jpg"
              />
            </div>
          )}

          <div className="space-y-2">
            <Label>Link to Partnership (optional)</Label>
            <Select 
              value={formData.partnership_id} 
              onValueChange={(v) => setFormData({ ...formData, partnership_id: v })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a partnership..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">None</SelectItem>
                {partnerships.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <Switch
              checked={formData.is_active}
              onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
            />
            <Label>Active (visible to users)</Label>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : banner ? 'Update Banner' : 'Create Banner'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default BannerManagement;
